package com.algorix.web

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.pointerevents.PointerEvent
import kotlin.js.json
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.cos
import kotlin.math.floor
import kotlin.math.max
import kotlin.math.pow
import kotlin.math.sin

class Slide(
    val app: String,
    val cap: String,
    val ios: Boolean,
    val srcEn: String,
    val srcTr: String?,
    val alt: String,
)

class AppInfo(val name: String, val cat: String, val st: String, val dot: String, val icon: String)

val APPS = mapOf(
    "scanme" to AppInfo("name.scanme", "cat.scanme", "st.scanme", "", "/assets/scanme.png"),
    "math" to AppInfo("name.math", "cat.math", "st.math", "", "/assets/basic-math.png"),
    "vault" to AppInfo("name.vault", "cat.vault", "st.vault", "is-part", "/assets/vaultly.png"),
)

val SLIDES = listOf(
    Slide("scanme", "cap.s04", true, "/assets/scanme-en-04-generated-qr.jpg", "/assets/scanme-tr-04-generated-qr.jpg", "sh.s04"),
    Slide("vault", "cap.v01", false, "/assets/vaultly-en-01-vault.jpg", null, "sh.v01"),
    Slide("math", "cap.m02", true, "/assets/basic-math-en-02-table.jpg", "/assets/basic-math-tr-02-table.jpg", "sh.m02"),
    Slide("scanme", "cap.s01", true, "/assets/scanme-en-01-scan-result.jpg", "/assets/scanme-tr-01-scan-result.jpg", "sh.s01"),
    Slide("vault", "cap.v03", false, "/assets/vaultly-en-03-generator.jpg", null, "sh.v03"),
    Slide("math", "cap.m04", true, "/assets/basic-math-en-04-teacher.jpg", "/assets/basic-math-tr-04-teacher.jpg", "sh.m04"),
    Slide("scanme", "cap.s09", true, "/assets/scanme-en-09-history-dark.jpg", "/assets/scanme-tr-09-history-dark.jpg", "sh.s09"),
    Slide("vault", "cap.v02", false, "/assets/vaultly-en-02-item.jpg", null, "sh.v02"),
    Slide("math", "cap.m01", true, "/assets/basic-math-en-01-home.jpg", "/assets/basic-math-tr-01-home.jpg", "sh.m01"),
)

private fun norm(a: Double): Double = ((a % 360) + 540) % 360 - 180
private fun jsRound(v: Double): Double = floor(v + 0.5)
private fun easeOut(p: Double): Double = 1 - (1 - p).pow(4)
private fun easeInOut(p: Double): Double = if (p < 0.5) 4 * p * p * p else 1 - (-2 * p + 2).pow(3) / 2

/**
 * The hero's 3D ring of devices: rotation, tweening, autoplay, drag/swipe and keyboard control.
 * Per-frame values are written straight to the items' CSS variables; the front/near classes and the
 * dock caption are Compose state.
 */
class Carousel(private val slides: List<Slide>) {
    private val reduce = reduceMotion
    val n = slides.size
    private val stepDeg = 360.0 / n

    // Current per-item ring values; also read by the composition so a recomposition re-applies them.
    val s = DoubleArray(n)
    val c = DoubleArray(n)
    val d = DoubleArray(n)

    /** Radius factor animated by the intro (null once settled). */
    var k: Double? = if (reduce) null else 0.14
        private set

    var front by mutableStateOf(0)
        private set
    var near by mutableStateOf(setOf<Int>())
        private set
    var playing by mutableStateOf(!reduce)
        private set
    var dragging by mutableStateOf(false)
        private set
    var liveOn by mutableStateOf(false)
        private set
    val canPause = !reduce

    val items = arrayOfNulls<HTMLElement>(n)
    var stage: HTMLElement? = null
    var ring: HTMLElement? = null
    var dock: HTMLElement? = null
    var capText: HTMLElement? = null
    var capScreen: HTMLElement? = null

    private var rot = 0.0
    private var tween: Tween? = null
    private var intro: Intro? = null
    private var drag: Drag? = null
    private var suppressClick = false
    private var hover = false
    private var focusIn = false
    private var inView = true
    private var autoT = 0
    private var capApp = slides.first().app
    private var capKey = slides.first().cap

    private class Tween(val from: Double, val to: Double, val t0: Double, val dur: Double, val ease: (Double) -> Double)
    private class Intro(val t0: Double, val dur: Double)
    private class Drag(val id: Int, var x0: Double, val y0: Double, var r0: Double, var lx: Double, var lt: Double) {
        var moved = false
        var v = 0.0
    }

    init {
        computeRing()
    }

    private fun computeRing() {
        val nearNow = mutableSetOf<Int>()
        for (i in 0 until n) {
            val a = norm(i * stepDeg - rot) * PI / 180
            c[i] = cos(a)
            s[i] = sin(a)
            d[i] = ((1 - c[i]) / 2).pow(0.6)
            if (c[i] > 0.3) nearNow += i
        }
        if (nearNow != near) near = nearNow
    }

    private fun renderRing() {
        computeRing()
        for (i in 0 until n) {
            val el = items[i] ?: continue
            el.cssVar("--s", s[i].fx(4))
            el.cssVar("--c", c[i].fx(4))
            el.cssVar("--d", d[i].fx(3))
        }
        val f = (((jsRound(rot / stepDeg).toInt()) % n) + n) % n
        if (f != front) {
            front = f
            updateCaption()
        }
    }

    /** Text that changes in the dock rises into place instead of cutting over. */
    private fun rise(el: HTMLElement?) {
        if (reduce || el == null || el.asDynamic().animate == undefined) return
        try {
            el.asDynamic().animate(
                arrayOf(json("opacity" to 0, "transform" to "translateY(6px)"), json("opacity" to 1, "transform" to "translateY(0)")),
                json("duration" to 280, "easing" to "cubic-bezier(.16,1,.3,1)"),
            )
        } catch (_: Throwable) {
        }
    }

    private fun updateCaption() {
        val slide = slides[front]
        if (slide.app != capApp) rise(capText)
        if (slide.cap != capKey) rise(capScreen)
        capApp = slide.app
        capKey = slide.cap
    }

    private fun goTo(target: Double, dur: Double, ease: (Double) -> Double) {
        if (reduce || dur <= 0) {
            tween = null
            rot = target
            renderRing()
            return
        }
        tween = Tween(rot, target, window.performance.now(), dur, ease)
        FrameLoop.kick()
    }

    private fun step(dir: Int, user: Boolean) {
        val base = tween?.to ?: (jsRound(rot / stepDeg) * stepDeg)
        goTo(base + dir * stepDeg, if (user) 850.0 else 1500.0, if (user) ::easeOut else ::easeInOut)
    }

    private fun goToIndex(i: Int) = goTo(rot + norm(i * stepDeg - rot), 900.0, ::easeOut)

    private fun live(on: Boolean) {
        liveOn = on
    }

    private fun canAuto() = playing && !reduce && inView && !hover && !focusIn && drag == null && intro == null && !document.asDynamic().hidden.unsafeCast<Boolean>()

    private fun schedule(ms: Int) {
        window.clearTimeout(autoT)
        if (playing && !reduce) autoT = window.setTimeout({ autoTick() }, ms)
    }

    private fun autoTick() {
        if (canAuto() && tween == null) {
            live(false)
            step(1, false)
            schedule(HOLD + 1500)
        } else {
            schedule(800)
        }
    }

    private fun userMoved() {
        live(true)
        schedule(HOLD * 2)
    }

    fun prev() {
        step(-1, true); userMoved()
    }

    fun next() {
        step(1, true); userMoved()
    }

    fun togglePlay() {
        playing = !playing
        if (playing) schedule(1200) else window.clearTimeout(autoT)
    }

    /** Frame handler; returns true while the intro or a tween is still running. */
    fun tick(now: Double): Boolean {
        var more = false
        var dirty = false
        intro?.let { ip0 ->
            val ip = clamp((now - ip0.t0) / ip0.dur, 0.0, 1.0)
            val kr = 0.14 + 0.86 * (1 - (1 - ip).pow(5))
            dirty = true
            if (ip >= 1) {
                k = null
                ring?.cssVar("--k", null)
                intro = null
                schedule(HOLD)
            } else {
                k = kr
                ring?.cssVar("--k", kr.fx(4))
                more = true
            }
        }
        tween?.let { tw ->
            val p = clamp((now - tw.t0) / tw.dur, 0.0, 1.0)
            rot = tw.from + (tw.to - tw.from) * tw.ease(p)
            dirty = true
            if (p >= 1) {
                rot = tw.to
                tween = null
            } else {
                more = true
            }
        }
        if (dirty) renderRing()
        return more
    }

    /** Wires events once the DOM exists. */
    fun start() {
        val stage = stage ?: return
        val ring = ring ?: return

        stage.addEventListener("keydown", { e ->
            e as KeyboardEvent
            when (e.key) {
                "ArrowRight" -> step(1, true)
                "ArrowLeft" -> step(-1, true)
                "Home" -> goToIndex(0)
                "End" -> goToIndex(n - 1)
                else -> return@addEventListener
            }
            e.preventDefault()
            userMoved()
        })
        ring.addEventListener("click", { e ->
            if (suppressClick) return@addEventListener
            val it = (e.target as? Element)?.closest(".item") ?: return@addEventListener
            val i = items.indexOfFirst { item -> item == it }
            if (i >= 0 && i != front) {
                goToIndex(i); userMoved()
            }
        })

        listOfNotNull(stage, dock).forEach { el ->
            el.addEventListener("pointerenter", { e -> if ((e as PointerEvent).pointerType == "mouse") hover = true })
            el.addEventListener("pointerleave", { e ->
                if ((e as PointerEvent).pointerType == "mouse") {
                    hover = false; schedule(HOLD)
                }
            })
            el.addEventListener("focusin", { focusIn = true })
            el.addEventListener("focusout", {
                window.setTimeout({
                    val a = document.activeElement
                    focusIn = a != null && (stage.contains(a) || dock?.contains(a) == true)
                    if (!focusIn) schedule(HOLD)
                }, 0)
            })
        }

        // drag / swipe
        stage.addEventListener("pointerdown", { e ->
            e as PointerEvent
            if (e.pointerType == "mouse" && e.button.toInt() != 0) return@addEventListener
            drag = Drag(e.pointerId, e.clientX.toDouble(), e.clientY.toDouble(), rot, e.clientX.toDouble(), e.timeStamp.toDouble())
        })
        stage.addEventListener("pointermove", { e ->
            e as PointerEvent
            val dr = drag ?: return@addEventListener
            if (e.pointerId != dr.id) return@addEventListener
            var dx = e.clientX - dr.x0
            val dy = e.clientY - dr.y0
            if (!dr.moved) {
                if (abs(dx) < 6) return@addEventListener
                if (abs(dy) > abs(dx)) {
                    drag = null; return@addEventListener
                }
                dr.moved = true
                dr.r0 = rot
                dr.x0 = e.clientX.toDouble()
                dx = 0.0
                tween = null
                try {
                    stage.setPointerCapture(e.pointerId)
                } catch (_: Throwable) {
                }
                dragging = true
                live(true)
            }
            val per = stepDeg / max(120.0, (items[0]?.offsetWidth ?: 0) * 1.2)
            rot = dr.r0 - dx * per
            val dt = max(1.0, e.timeStamp.toDouble() - dr.lt)
            dr.v = -(e.clientX - dr.lx) / dt * per
            dr.lx = e.clientX.toDouble()
            dr.lt = e.timeStamp.toDouble()
            renderRing()
        })
        val endDrag: (Event) -> Unit = end@{
            val dr = drag ?: return@end
            if (dr.moved) {
                suppressClick = true
                window.setTimeout({ suppressClick = false }, 60)
                val proj = rot + clamp(dr.v, -1.2, 1.2) * 160
                goTo(jsRound(proj / stepDeg) * stepDeg, 750.0, ::easeOut)
                dragging = false
                userMoved()
            }
            drag = null
        }
        stage.addEventListener("pointerup", endDrag)
        stage.addEventListener("pointercancel", endDrag)
        stage.addEventListener("lostpointercapture", endDrag)

        if (hasIntersectionObserver) {
            IntersectionObserver({ en, _ ->
                val was = inView
                inView = en[0].isIntersecting && en[0].intersectionRatio > 0.25
                if (inView && !was) schedule(HOLD)
            }, json("threshold" to arrayOf(0, 0.25, 0.5))).observe(stage)
        }

        document.addEventListener("visibilitychange", {
            val hidden = document.asDynamic().hidden.unsafeCast<Boolean>()
            document.documentElement?.classList?.toggle("is-hidden", hidden)
            if (!hidden) schedule(HOLD)
        })

        FrameLoop.add(::tick)
        if (!reduce) {
            intro = Intro(window.performance.now() + 60, 1050.0)
            // Guarantees the final layout even if animation frames are throttled.
            window.setTimeout({
                if (intro != null) {
                    intro = null
                    k = null
                    ring.cssVar("--k", null)
                    schedule(HOLD)
                    renderRing()
                }
            }, 1500)
            FrameLoop.kick()
        }
    }

    private companion object {
        const val HOLD = 3800
    }
}
